import {useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';
import Select from 'react-select';
import * as yup from 'yup';
import {useFormik} from 'formik';
import Button from '../../common/Button';
import Input from '../../common/simple/input';
import Checkbox from '../../common/simple/Checkbox';
import { constants } from '../../common/constants/constants';
import { newGraySelectStyles } from '../../common/shared/select.styled';
import arrowInCircle from '../../images/newDesign/arrow_in_white_circle.svg';
import PaymentFlowContainer from './PaymentFlowContainer';
import StringUtils from "../../common/utils/strings";
import {setTenantDetails} from "../../store/tenant/slice";
import { translatesLegalInfoPages } from "../../common/constants/translatedLegalInfoLinks";

export function TenantDetails({ match }) {

    const { buildingID } = match.params;

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const history = useHistory();

    document.title = t('titles.tenant_details');

    const tenantDetails = useSelector(state => state.tenant.details);
    const buildingApartments = useSelector(state => state.tenant.buildingApartments ?? []);
    const currentBuildingData = useSelector(state => state.buildings.currentBuildingData);

    const externalPartnerId = currentBuildingData?.managementCompany?.externalId;
    const allowedExternalPartnerIds = constants.newFlowBuildingsExclude; // requested by rayee via email

    const isEntityAllowed = !allowedExternalPartnerIds.includes(externalPartnerId);


    const formSchema = useMemo(() => yup.object().shape({
        apartmentNumber: yup.object()
            .required(t('titles.forms.mandatory_field')).nullable(),
        tenant_type: yup.object()
            .required(t('titles.forms.mandatory_field')).nullable(),
        name: yup.string()
            .required(t('titles.forms.mandatory_field_full_name'))
            .min(2, t('titles.forms.min_length', {size: 2})),
        phone: yup.string()
            .required(t('titles.forms.mandatory_field'))
            .matches(/^[0-9]+$/, t('titles.forms.correct_phone'))
            .min(9, t('titles.forms.min_length', {size: 9}))
            .max(10, t('titles.forms.max_length', {size: 10})),
            // copied from old form
            //  due to errors https://bllink.sentry.io/issues/3764974514/events/056d934ba5d646c79bf8aa0eaa8447e5/?project=5402476
            // .test('isValidPhone', t('titles.forms.correct_phone'), (value, testContext) => {
            //     const trimmed = String(value ?? '').replaceAll(/[ -]/g, '');
            //     // do we need check country code (972) here?
            //     const fullNumber = trimmed.match(/^\+[0-9]{12}$/);
            //     const shortNumber = trimmed.match(/^[0-9]{7,12}$/);
            //     return fullNumber != null || shortNumber != null;
            // }),
        email: yup.string()
            // change to regex since the FE and BE (sequlize isEmail) definition of an email are different
            .matches(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/, t('titles.forms.correct_email'))
            // .email(t('titles.forms.correct_email'))
            .optional(),
        comment: yup.string()
            .max(100, t('titles.forms.max_length_exceeded', 100))
            .optional(),
        terms_of_service_confirm: yup.boolean(),
        agrees_to_ads: yup.boolean(),
    }), []);
    const apartmentDropdownOptions = useMemo(() => buildingApartments?.map(num => ({ label: num, value: num })), [buildingApartments]);
    const ownershipDropdownOptions = useMemo(() => ([
        { value: constants.APIContract.tenantTypes.renter, label: t('payments.renter') },
        { value: constants.APIContract.tenantTypes.owner, label: t('payments.owner') },
    ]), []);

    const formik = useFormik({
        initialValues: {
            apartmentNumber: '',
            tenant_type: '',
            name: '',
            phone: '',
            email: '',
            comment: '',
            terms_of_service_confirm: false,
            agrees_to_ads: false,
        },
        validationSchema: formSchema,
        onSubmit: handleFormSubmit
    });

    useEffect(async () => {
        if (tenantDetails != null) {
            const apartmentNumber = apartmentDropdownOptions.find(option => option.value === tenantDetails?.apartmentNumber);
            const tenant_type = ownershipDropdownOptions.find(option => option.value === tenantDetails?.tenant_type);
            await formik.setValues({ ...tenantDetails, apartmentNumber, tenant_type });
        }
    }, [tenantDetails]);

    function saveEmailToSession(phone, email) {
        const userID = email || phone;
        if (window.smartlook) window.smartlook('identify', userID);

        // closing tenants for now
        // if (window.userpilot) window.userpilot.identify( userID, { email, isTenant: true });
    }

    async function saveDetailsAndGoToNextPage(data) {
        const apartmentNumber = data.apartmentNumber.value;
        const tenantDetails = {...data, apartmentNumber: data.apartmentNumber.value, tenant_type: data.tenant_type.value, buildingID};

        saveEmailToSession(data.phone, data.email);
        dispatch(setTenantDetails(tenantDetails));

        let nextPageUrl;
        // TODO: remove after opening to all buildings
        if (isEntityAllowed){
            nextPageUrl = `/${constants.pages.paymentSelectionUrl}-new/${buildingID}/${apartmentNumber}`
        }
        else{
            nextPageUrl = `/${constants.pages.paymentSelectionUrl}/${buildingID}/${apartmentNumber}`
        }
        history.push({ pathname: nextPageUrl });
    }

    async function handleFormSubmit(values, helpers) {
        if (!values.terms_of_service_confirm) {
            NotificationManager.error(t('titles.forms.subscribe_check'))
        } else {
            const parsedValues = {
                ...values,
                email: StringUtils.sanitizeString(values.email),
                phone: StringUtils.sanitizeString(values.phone),
            }
            await saveDetailsAndGoToNextPage(parsedValues);
            helpers.reset();
        }
    }

    function checkForErrors(errors) {
        if (Object.keys(errors).length > 0) {
            NotificationManager.error(t('payments.please_fill'));
        }
    }

    function ErrorMessage({ name, children }) {
        if (formik.touched[name] && typeof formik.errors[name] === 'string') {
            return children(formik.errors[name]);
        }
        return '';
    }

    return (
        <PaymentFlowContainer title={t('reports.payment_for')} subheader={t('payments.fill_details')} showHeaderMessage>

            <div className='credit-card-and-actions-block'>
                            <form onSubmit={formik.handleSubmit}>
                                <div>
                                    <label className='label' htmlFor="apartmentNumber">* {t('reports.apt')}</label>
                                    <Select
                                        styles={newGraySelectStyles}
                                        options={apartmentDropdownOptions}
                                        name='apartmentNumber'
                                        onChange={e => formik.setFieldValue('apartmentNumber', e)}
                                        onBlur={() => formik.setFieldTouched('apartmentNumber', true)}
                                        value={formik.values.apartmentNumber}
                                        placeholder={t('payments.select_placeholder')}
                                        isSearchable={false}
                                    />
                                    <ErrorMessage name="apartmentNumber">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                </div>

                                <div>
                                    <label className='label' htmlFor="ownership">* {t('payments.type_of_tenant')}</label>
                                    <Select
                                        styles={newGraySelectStyles}
                                        options={ownershipDropdownOptions}
                                        name='tenant_type'
                                        onChange={e => formik.setFieldValue('tenant_type', e)}
                                        onBlur={() => formik.setFieldTouched('tenant_type', true)}
                                        value={formik.values.tenant_type}
                                        placeholder={t('payments.select_placeholder')}
                                        isSearchable={false}
                                    />
                                    <ErrorMessage name="tenant_type">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                </div>

                                <div>
                                    <Input
                                        name='name'
                                        label={`* ${t('payments.full_name')}`}
                                        autocomplete='name'
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <ErrorMessage name="name">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                </div>

                                <div>
                                    <Input
                                        name='phone'
                                        label={`* ${t('reports.tenantPhone')}`}
                                        autocomplete='tel'
                                        value={formik.values.phone}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        inputMode="tel"
                                    />
                                    <ErrorMessage name="phone">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                </div>

                                <div className='full-width'>
                                    <Input
                                        name='email'
                                        label={`${t('payments.mail')}:`}
                                        autocomplete='email'
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    <ErrorMessage name="email">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                </div>

                                <div className='full-width'>
                                    <div className='text-area'>
                                        <label className='label' htmlFor="comment">{t('expenses.comment')}</label>
                                        <textarea
                                            name='comment'
                                            value={formik.values.comment}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            id="comment"
                                            rows="5"
                                        />
                                    </div>
                                    <ErrorMessage name="comment">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                </div>

                                <div className='checkboxes'>
                                    <div className='check'>
                                        <Checkbox
                                            id="agrees_to_ads"
                                            name="agrees_to_ads"
                                            checked={formik.values.agrees_to_ads}
                                            onChange={value => formik.setFieldValue('agrees_to_ads', value)}
                                        />
                                        {t('payments.confirm_mail')}
                                    </div>

                                    <div className='check'>
                                        <Checkbox
                                            id="tos-confirm"
                                            name="terms_of_service_confirm"
                                            checked={formik.values.terms_of_service_confirm}
                                            onChange={value => formik.setFieldValue('terms_of_service_confirm', value)}
                                        />
                                        <span>
                                            {t('payments.terms_of_use')}&nbsp;
                                            <a rel="noopener noreferrer" target="_blank" id="tos-link" href={translatesLegalInfoPages.termsOfUseUrl.default}>
                                                <span>{t('payments.tos')}</span>
                                            </a>
                                        </span>
                                    </div>
                                </div>

                                <Button type="submit" onClick={() => checkForErrors(formik.errors)}>{t('expenses.set_expenses.continue')} <img className='arrow' src={arrowInCircle} /></Button>
                            </form>
            </div>
        </PaymentFlowContainer>
    )
}
